// Pure decision logic for the "new version available" banner.
//
// Why this lives in its own module (not buried in the React hook/component):
// it is the one piece of behaviour that MUST be exhaustively provable offline
// — the difference between "silently nag on every load" and "only nag when the
// tab is genuinely running an old bundle". House precedent: order-totals /
// marginRepriceWrites — keep the decision pure, import it from both the
// component AND a gate that executes every case. See
// scripts/qa-version-check.ts.
//
// THE SHARP EDGE (why we normalise before comparing):
//   GET /health returns `build` = the git SHA sliced to 7 chars
//   (apps/api/src/app.ts — `buildSha.slice(0, 7)`), while the web bundle
//   embeds `RAILWAY_GIT_COMMIT_SHA`, which Railway supplies as the FULL
//   40-char SHA. A naïve `full !== short` comparison is ALWAYS true, so the
//   banner would appear on every page load of a perfectly-fresh deploy. We
//   therefore compare the first 7 chars of each — the same short form the
//   server publishes.

/** The short-SHA length the server's /health endpoint publishes. */
const SHORT_SHA_LEN = 7;

/**
 * Reduce a raw build value to a comparable short SHA, or `null` when it
 * carries no usable information. `null`, `undefined`, empty/whitespace, and
 * the literal `"unknown"` sentinel (what /health returns when no SHA is set)
 * all collapse to `null` — i.e. "we don't know, so never nag".
 */
function normalizeSha(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "unknown") return null;
  return trimmed.slice(0, SHORT_SHA_LEN);
}

/**
 * Should the "new version available" banner be shown?
 *
 * TRUE only when BOTH sides carry a real, known SHA and those SHAs differ
 * (compared in the server's short-SHA form). Any missing / unknown / empty
 * value → FALSE. This is deliberately conservative: on a flaky network or an
 * unverifiable build we stay silent rather than nag.
 *
 * @param myBuildSha     the SHA the running bundle was built from (`__BUILD_SHA__`)
 * @param serverBuildSha the SHA the server currently reports (`/health` → `build`)
 */
export function shouldShowUpdateBanner(
  myBuildSha: string | null | undefined,
  serverBuildSha: string | null | undefined,
): boolean {
  const mine = normalizeSha(myBuildSha);
  const server = normalizeSha(serverBuildSha);
  if (mine === null || server === null) return false;
  return mine !== server;
}
